import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { AxiosInstance } from 'axios';
import {
  LoadDataAction,
  EntityState,
  DataPayload,
  SortPayloadAction,
} from '../types';
import { Auth } from '../auth/types';
import {
  AuditAction,
  AuditEntityType,
  AuditLogEntity,
  AuditTrailFilters,
} from './types';
import AuditTrailApi from '../../api/auditTrailApi';
import { downloadBlob } from '../../utils/downloadFile';
import { showToast } from '../../aurora/components/Toast/Toast';

export interface AuditTrailState extends EntityState<AuditLogEntity> {
  selectedFilters?: AuditTrailFilters;
  totalResults: number;
}

const initialState: AuditTrailState = {
  data: [],
  currentPage: 0,
  resultsPerPage: 10,
  totalPages: 0,
  totalResults: 0,
  sortColumn: 'createdAt',
  sortDirection: -1,
  selectedFilters: {},
};

export const downloadAuditCsvAction = createAsyncThunk(
  'auditTrail/downloadCsv',
  async ({
    axios,
    auth,
    filters,
    includePii,
  }: {
    axios: AxiosInstance;
    auth: Auth | null;
    filters?: AuditTrailFilters;
    includePii?: boolean;
  }) => {
    return AuditTrailApi.downloadCsv(axios, auth, filters, includePii);
  }
);

export const auditCsvFilename = () =>
  `audit-trail_${new Date().toISOString().slice(0, 10)}.csv`;

const auditTrailSlice = createSlice({
  name: 'auditTrail',
  initialState,
  reducers: {
    setAuditLogs: (
      state,
      {
        payload,
      }: PayloadAction<DataPayload<AuditLogEntity> & { totalResults?: number }>
    ) => {
      state.data = payload.entities;
      state.totalPages = payload.totalPages;
      state.totalResults = payload.totalResults ?? 0;
    },
    sortAuditLogs: (state, { payload }: SortPayloadAction<AuditLogEntity>) => {
      state.sortColumn = payload.column;
      state.sortDirection = payload.direction;
    },
    setDateFilter: (
      state,
      { payload }: PayloadAction<[string | undefined, string | undefined]>
    ) => {
      const [startDate, endDate] = payload;
      state.selectedFilters = Object.assign({}, state.selectedFilters, {
        startDate,
        endDate,
      });
      state.currentPage = initialState.currentPage;
    },
    setActorIdsFilter: (
      state,
      { payload }: PayloadAction<string[] | undefined>
    ) => {
      state.selectedFilters = Object.assign({}, state.selectedFilters, {
        actorIds: payload,
      });
      state.currentPage = initialState.currentPage;
    },
    setActionsFilter: (
      state,
      { payload }: PayloadAction<AuditAction[] | undefined>
    ) => {
      state.selectedFilters = Object.assign({}, state.selectedFilters, {
        actions: payload,
      });
      state.currentPage = initialState.currentPage;
    },
    setEntityTypesFilter: (
      state,
      { payload }: PayloadAction<AuditEntityType[] | undefined>
    ) => {
      state.selectedFilters = Object.assign({}, state.selectedFilters, {
        entityTypes: payload,
      });
      state.currentPage = initialState.currentPage;
    },
    setCompanyIdsFilter: (
      state,
      { payload }: PayloadAction<string[] | undefined>
    ) => {
      state.selectedFilters = Object.assign({}, state.selectedFilters, {
        companyIds: payload,
      });
      state.currentPage = initialState.currentPage;
    },
    setCurrentPage: (state, { payload }: PayloadAction<number>) => {
      state.currentPage = payload;
    },
    setResultsPerPage: (state, { payload }: PayloadAction<number>) => {
      state.resultsPerPage = payload;
      state.currentPage = 0;
    },
    resetPage: (state) => {
      state.selectedFilters = initialState.selectedFilters;
      state.sortColumn = initialState.sortColumn;
      state.sortDirection = initialState.sortDirection;
      state.currentPage = initialState.currentPage;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(downloadAuditCsvAction.pending, () => {
      showToast({
        type: 'info',
        title: 'Downloading',
        text: 'Preparing your audit trail export. Please wait...',
      });
    });
    builder.addCase(downloadAuditCsvAction.fulfilled, (state, action) => {
      const filename = auditCsvFilename();
      downloadBlob(action.payload, filename);
      showToast({
        type: 'success',
        title: `${filename} downloading`,
      });
    });
    builder.addCase(downloadAuditCsvAction.rejected, () => {
      showToast({
        type: 'error',
        title: 'Export failed',
        text: 'Could not export the audit trail. Please try again.',
      });
    });
  },
});

export const loadAuditLogs: LoadDataAction<AuditLogEntity> = async (
  axios: AxiosInstance,
  auth: Auth,
  state: EntityState<AuditLogEntity>
) => {
  const {
    resultsPerPage,
    currentPage,
    sortColumn,
    sortDirection,
    selectedFilters,
  } = state;

  const { entities, totalPages, totalResults } =
    await AuditTrailApi.getAuditLogs(
      axios,
      auth,
      resultsPerPage,
      currentPage,
      String(sortColumn),
      sortDirection,
      selectedFilters
    );

  return setAuditLogs({ entities, totalPages, totalResults });
};

export const {
  setAuditLogs,
  sortAuditLogs,
  setDateFilter,
  setActorIdsFilter,
  setActionsFilter,
  setEntityTypesFilter,
  setCompanyIdsFilter,
  setCurrentPage,
  setResultsPerPage,
  resetPage,
} = auditTrailSlice.actions;

export default auditTrailSlice.reducer;
